import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { AuthModal } from '../components/AuthModal';
import { SignupModal } from '../components/SignupModal';
import { RequestInvitationModal } from '../components/RequestInvitationModal';
import { 
  Trophy, Users, ArrowRight, MapPin, Calendar, MessageCircle, 
  BarChart3, Check, Crown, Camera, Zap, Globe, Heart, Shield, Clock,
  ThumbsUp, MessageSquare, Share2, Flame, Flag, Plane, Map, Briefcase
} from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1565527004647-3e729d98e2ee?w=1920&q=80';
const SUNSET_IMG = 'https://images.unsplash.com/photo-1636315662384-8406112deaf1?w=1920&q=80';

export default function Landing() {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [signupPrefillCode, setSignupPrefillCode] = useState('');
  const [signupPrefill, setSignupPrefill] = useState(null);
  const [showInviteRequestModal, setShowInviteRequestModal] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [isVisible, setIsVisible] = useState({});
  const [promoVideoUrl, setPromoVideoUrl] = useState(null);
  const [isMuted, setIsMuted] = useState(true);
  const [feedTab, setFeedTab] = useState('live');
  const videoRef = useRef(null);

  useEffect(() => { if (user) navigate('/dashboard'); }, [user, navigate]);

  // Approval emails send users to `/?signup=1&code=XXX` so the signup modal opens
  // automatically with their invite code already filled in.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('signup') === '1') {
      const code = (params.get('code') || '').trim();
      if (code) setSignupPrefillCode(code);
      setShowSignupModal(true);
      // Clean up the URL so back/refresh doesn't keep re-triggering
      const clean = new URL(window.location.href);
      clean.searchParams.delete('signup');
      clean.searchParams.delete('code');
      window.history.replaceState({}, '', clean.toString());
    }
  }, []);

  useEffect(() => {
    fetch(`${API}/promo-video-status`)
      .then(r => r.json())
      .then(d => {
        if (d.ready) {
          const origin = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
            ? (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001') : window.location.origin;
          setPromoVideoUrl(`${origin}${d.url}?v=${Date.now()}`);
        }
      }).catch(() => {});
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) setIsVisible(p => ({ ...p, [e.target.id]: true })); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('[data-animate]').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const handleGetStarted = () => setShowInviteRequestModal(true);
  const handleLogin = () => { setAuthTab('login'); setShowAuthModal(true); };
  const handleAuthSuccess = (u) => { setUser(u); navigate('/dashboard'); };

  return (
    <div className="min-h-screen bg-[#0b0f0b] text-white overflow-x-hidden">

      {/* ===== NAV ===== */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0b0f0b]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
              <Trophy className="w-4 h-4 text-black" />
            </div>
            <span className="font-black text-sm tracking-tight">THE BUNDLE GOLF CLUB</span>
          </div>
          <div className="hidden md:flex items-center gap-5 text-xs text-gray-400">
            <a href="/become-admin" className="hover:text-white transition-colors">Directors</a>
            <a href="/fundraiser" className="hover:text-white transition-colors">Fundraisers</a>
            <a href="/sponsors" className="hover:text-white transition-colors">Sponsors</a>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleLogin} className="text-gray-400 hover:text-white text-xs px-3 py-1.5">Sign In</button>
            <button onClick={handleGetStarted} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs px-4 py-1.5 rounded-lg transition-colors">Request Invitation</button>
          </div>
        </div>
      </nav>

      {/* ===== HERO ===== */}
      <section className="relative min-h-screen flex items-end pb-12 pt-14">
        <div className="absolute inset-0">
          {promoVideoUrl ? (
            <>
              <video ref={videoRef} autoPlay muted={isMuted} loop playsInline className="w-full h-full object-cover pointer-events-none"
                onCanPlay={() => { if (videoRef.current) videoRef.current.play().catch(() => {}); }}>
                <source src={promoVideoUrl} type="video/mp4" />
              </video>
              <button data-testid="video-sound-toggle" onClick={() => { setIsMuted(!isMuted); if (videoRef.current) videoRef.current.muted = !isMuted; }}
                className="absolute bottom-28 right-4 z-20 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-black/60 transition-colors">
                {isMuted ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                )}
              </button>
            </>
          ) : (
            <img src={HERO_IMG} alt="" className="w-full h-full object-cover pointer-events-none" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0f0b] via-[#0b0f0b]/60 to-transparent pointer-events-none" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[11px] text-emerald-300 font-medium">Live games happening now</span>
            </div>
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-bundle-gold mb-3" data-testid="hero-welcome">
              Welcome to Bundle Golf Club
            </div>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-4" data-testid="hero-headline">
              Where Golf Meets <span className="text-emerald-400">Networking</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-300 mb-6 max-w-xl leading-relaxed" data-testid="hero-subline">
              The nation&apos;s golf networking platform connecting golfers, businesses, tournament organizers, sponsors, fundraisers, and golf courses through one powerful community.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button data-testid="hero-find-game" onClick={handleGetStarted} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-6 py-5 rounded-xl text-sm">
                Join as a Golfer <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button onClick={() => navigate('/become-admin')} variant="outline" className="border-white/20 text-white hover:bg-white/10 font-semibold px-6 py-5 rounded-xl text-sm">
                Become a Director
              </Button>
              <Button onClick={() => navigate('/fundraiser')} variant="outline" className="border-white/20 text-white hover:bg-white/10 font-semibold px-6 py-5 rounded-xl text-sm">
                Start a Fundraiser
              </Button>
              <Button onClick={() => navigate('/sponsors')} variant="outline" data-testid="hero-sponsor-btn" className="border-blue-400/30 text-blue-200 hover:bg-blue-500/10 font-semibold px-6 py-5 rounded-xl text-sm">
                Become a Sponsor
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 1.5: FIND YOUR GOLF COMMUNITY (Bundle Tags) ===== */}
      <section className="py-14 relative border-y border-white/5 bg-gradient-to-b from-transparent via-emerald-950/10 to-transparent" id="tags" data-animate>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-3">
              <span className="text-emerald-400 text-[11px] font-bold uppercase tracking-wider">Bundle Tags</span>
            </div>
            <h2 className="text-2xl sm:text-4xl font-black" data-testid="tags-heading">
              Find Your <span className="text-emerald-400">Golf Community</span>
            </h2>
            <p className="text-gray-400 text-sm sm:text-base mt-3 max-w-2xl mx-auto leading-relaxed">
              Connect with golfers who share your profession, interests, military service, fraternity or sorority, favorite teams, travel destinations, hobbies, and more.
            </p>
          </div>

          <p className="text-center text-[11px] text-gray-500 font-bold uppercase tracking-[0.2em] mb-4">Popular Tags</p>

          <div className="flex flex-wrap justify-center gap-2 sm:gap-3" data-testid="popular-tags-list">
            {[
              { emoji: '🏌️', tag: 'GolfTravel', color: 'emerald' },
              { emoji: '✈️',  tag: 'AirForce',    color: 'blue' },
              { emoji: '🏢', tag: 'Entrepreneur', color: 'yellow' },
              { emoji: '🍷', tag: 'Wine',         color: 'red' },
              { emoji: '🏠', tag: 'Realtor',      color: 'orange' },
              { emoji: '💼', tag: 'SmallBusiness',color: 'sky' },
              { emoji: '🌴', tag: 'Florida',      color: 'teal' },
              { emoji: '🎓', tag: 'HowardUniversity', color: 'red' },
              { emoji: '💚', tag: 'AKA',          color: 'emerald' },
              { emoji: '💜', tag: 'OmegaPsiPhi',  color: 'purple' },
              { emoji: '⚪', tag: 'KappaAlphaPsi',color: 'zinc' },
            ].map(({ emoji, tag, color }) => {
              const colorMap = {
                emerald: 'border-emerald-500/25 hover:border-emerald-400/60 hover:bg-emerald-500/10 hover:text-emerald-200',
                blue:    'border-blue-500/25 hover:border-blue-400/60 hover:bg-blue-500/10 hover:text-blue-200',
                yellow:  'border-yellow-500/25 hover:border-yellow-400/60 hover:bg-yellow-500/10 hover:text-yellow-100',
                red:     'border-red-500/25 hover:border-red-400/60 hover:bg-red-500/10 hover:text-red-200',
                orange:  'border-orange-500/25 hover:border-orange-400/60 hover:bg-orange-500/10 hover:text-orange-200',
                sky:     'border-sky-500/25 hover:border-sky-400/60 hover:bg-sky-500/10 hover:text-sky-200',
                teal:    'border-teal-500/25 hover:border-teal-400/60 hover:bg-teal-500/10 hover:text-teal-200',
                purple:  'border-purple-500/25 hover:border-purple-400/60 hover:bg-purple-500/10 hover:text-purple-200',
                zinc:    'border-zinc-500/30 hover:border-zinc-300/60 hover:bg-zinc-400/10 hover:text-white',
              };
              return (
                <button
                  key={tag}
                  onClick={handleGetStarted}
                  data-testid={`popular-tag-${tag}`}
                  className={`group inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.03] border ${colorMap[color]} text-gray-300 text-xs sm:text-sm font-semibold transition-all duration-200 hover:scale-[1.03] hover:-translate-y-0.5 active:scale-95`}
                >
                  <span aria-hidden="true" className="text-base leading-none">{emoji}</span>
                  <span className="whitespace-nowrap">#{tag}</span>
                </button>
              );
            })}
          </div>

          <div className="text-center mt-8">
            <button
              onClick={handleGetStarted}
              data-testid="tags-cta"
              className="inline-flex items-center gap-1.5 text-emerald-400 text-sm font-bold hover:text-emerald-300 transition-colors"
            >
              Join to browse all Bundle Tags <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ===== SECTION 2: LIVE GOLF ACTIVITY — THE HEART ===== */}
      <section className="py-10 relative" id="live-feed" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 mb-3">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[11px] text-red-400 font-bold uppercase tracking-wider">Live Golf Activity</span>
            </div>
            <h2 className="text-2xl sm:text-4xl font-black">
              Golf is happening right now — <span className="text-emerald-400">jump in.</span>
            </h2>
            <p className="text-gray-500 text-sm mt-2">Live games, open spots, and real-time results</p>
          </div>

          {/* Filter Tabs */}
          <div className="flex items-center justify-center gap-2 mb-6 overflow-x-auto pb-1" data-testid="feed-filter-tabs">
            {[
              { key: 'live', label: 'Live Now', icon: <Flame className="w-3.5 h-3.5" />, color: 'red' },
              { key: 'my', label: 'My Games', icon: <Users className="w-3.5 h-3.5" />, color: 'emerald' },
              { key: 'nearby', label: 'Nearby', icon: <Globe className="w-3.5 h-3.5" />, color: 'blue' },
              { key: 'results', label: 'Results', icon: <Trophy className="w-3.5 h-3.5" />, color: 'yellow' },
            ].map(tab => (
              <button key={tab.key} data-testid={`feed-tab-${tab.key}`}
                onClick={() => setFeedTab(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
                  feedTab === tab.key
                    ? tab.color === 'red' ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                    : tab.color === 'emerald' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : tab.color === 'blue' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    : 'bg-white/5 text-gray-500 border border-white/5 hover:border-white/10 hover:text-gray-300'
                }`}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* Feed Grid — 2 columns on mobile, 4 on desktop */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">

            {/* 1: LIVE — Birdie Alert (Mike T) */}
            {(feedTab === 'live' || feedTab === 'my') && (
            <div className={`col-span-2 lg:col-span-1 bg-[#131a13] rounded-2xl border border-white/5 p-4 sm:p-5 hover:border-emerald-500/30 transition-all duration-500 ${isVisible['live-feed'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} data-testid="feed-card-birdie">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">MT</div>
                  <div>
                    <span className="text-sm font-bold">Mike T</span>
                    <span className="text-[10px] text-gray-500 block">2 min ago</span>
                  </div>
                </div>
                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-[10px] font-bold rounded-full flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" /> LIVE
                </span>
              </div>
              <p className="text-sm mb-1"><Flame className="w-4 h-4 text-orange-400 inline mr-1" /><span className="font-bold">Birdied Hole 9!</span></p>
              <p className="text-xs text-gray-500 flex items-center gap-1 mb-3"><MapPin className="w-3 h-3" /> Lake Presidential Golf Club</p>
              <div className="flex items-center gap-4 text-gray-500 border-t border-white/5 pt-2">
                <button className="flex items-center gap-1 text-xs hover:text-emerald-400 transition-colors"><ThumbsUp className="w-3.5 h-3.5" /> 24</button>
                <button className="flex items-center gap-1 text-xs hover:text-emerald-400 transition-colors"><Flame className="w-3.5 h-3.5" /> 18</button>
                <button className="flex items-center gap-1 text-xs hover:text-emerald-400 transition-colors"><MessageSquare className="w-3.5 h-3.5" /> 5</button>
                <button className="ml-auto flex items-center gap-1 text-xs hover:text-emerald-400 transition-colors"><Share2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            )}

            {/* 2: NEARBY — Open Game */}
            {(feedTab === 'live' || feedTab === 'nearby') && (
            <div className={`bg-[#0f1a12] rounded-2xl border border-emerald-500/30 p-4 sm:p-5 hover:border-emerald-500/50 transition-all duration-500 relative overflow-hidden ${isVisible['live-feed'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '80ms' }} data-testid="feed-card-open-game">
              <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/5 rounded-full -mr-6 -mt-6" />
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">Open Game</span>
                <span className="text-[10px] text-yellow-400 font-bold animate-pulse">3 spots left</span>
              </div>
              <p className="text-sm font-bold mb-0.5">Today at 2:10 PM</p>
              <p className="text-xs text-gray-500 flex items-center gap-1 mb-3"><MapPin className="w-3 h-3" /> East Potomac Golf Links</p>
              <div className="flex items-center gap-1.5 mb-3">
                {['JD', 'KL', 'AS'].map((init, i) => (
                  <div key={i} className="w-7 h-7 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-[9px] font-bold text-emerald-300">{init}</div>
                ))}
                <span className="text-[10px] text-gray-500 ml-1">+3 joined</span>
              </div>
              <button onClick={handleGetStarted} className="w-full py-2.5 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs rounded-lg transition-colors">
                Join Game
              </button>
            </div>
            )}

            {/* 3: LIVE — Scorecard Posted */}
            {(feedTab === 'live' || feedTab === 'results') && (
            <div className={`bg-[#131a13] rounded-2xl border border-white/5 p-4 sm:p-5 hover:border-emerald-500/30 transition-all duration-500 ${isVisible['live-feed'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '160ms' }} data-testid="feed-card-scorecard">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-xs font-bold">DC</div>
                  <div>
                    <span className="text-sm font-bold">Devin C</span>
                    <span className="text-[10px] text-gray-500 block">15 min ago</span>
                  </div>
                </div>
                <Camera className="w-4 h-4 text-gray-500" />
              </div>
              <p className="text-sm mb-1"><span className="font-bold">Posted a scorecard</span></p>
              <div className="flex items-center gap-3 text-xs mb-3">
                <span className="text-emerald-400 font-bold">78</span>
                <span className="text-gray-500">|</span>
                <span className="text-gray-400">Renditions Golf Course</span>
              </div>
              <div className="bg-white/5 rounded-lg p-2 mb-3">
                <div className="grid grid-cols-9 gap-0.5 text-[9px] text-center text-gray-500">
                  {[4,5,3,4,5,4,3,5,4].map((s, i) => (
                    <div key={i}>
                      <div className="text-gray-600">{i+1}</div>
                      <div className={`font-bold ${s < 4 ? 'text-emerald-400' : s > 4 ? 'text-red-400' : 'text-white'}`}>{s}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4 text-gray-500">
                <button className="flex items-center gap-1 text-xs hover:text-emerald-400 transition-colors"><ThumbsUp className="w-3.5 h-3.5" /> 9</button>
                <button className="flex items-center gap-1 text-xs hover:text-emerald-400 transition-colors"><MessageSquare className="w-3.5 h-3.5" /> 2</button>
              </div>
            </div>
            )}

            {/* 4: RESULTS — Saturday Skins Game */}
            {(feedTab === 'live' || feedTab === 'results') && (
            <div className={`bg-[#131a13] rounded-2xl border border-white/5 p-4 sm:p-5 hover:border-emerald-500/30 transition-all duration-500 ${isVisible['live-feed'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '240ms' }} data-testid="feed-card-results">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-bold text-yellow-400 uppercase tracking-wider flex items-center gap-1"><Trophy className="w-3 h-3" /> Final Results</span>
                <span className="text-[10px] text-gray-500">2h ago</span>
              </div>
              <p className="text-sm font-bold mb-2">Saturday Skins Game</p>
              <div className="space-y-1.5 mb-3">
                {[
                  { name: 'John D', score: '-2', rank: '1st', color: 'text-yellow-400' },
                  { name: 'Mike T', score: '+1', rank: '2nd', color: 'text-gray-300' },
                  { name: 'Dave K', score: '+3', rank: '3rd', color: 'text-orange-400' },
                ].map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className={`font-bold ${p.color}`}>{p.rank}</span>
                      <span className="text-gray-300">{p.name}</span>
                    </div>
                    <span className={`font-bold ${p.score.startsWith('-') ? 'text-emerald-400' : 'text-red-400'}`}>{p.score}</span>
                  </div>
                ))}
              </div>
              <button className="text-xs text-emerald-400 font-bold hover:underline">View Leaderboard</button>
            </div>
            )}

            {/* 5: NEARBY — Upcoming Scramble */}
            {(feedTab === 'live' || feedTab === 'nearby') && (
            <div className={`bg-[#0f1a12] rounded-2xl border border-emerald-500/20 p-4 sm:p-5 hover:border-emerald-500/40 transition-all duration-500 ${isVisible['live-feed'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '320ms' }} data-testid="feed-card-upcoming">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">Tomorrow</span>
                <span className="text-[10px] text-yellow-400 font-bold">5 spots left</span>
              </div>
              <p className="text-sm font-bold mb-0.5">Sunday Scramble — 9:00 AM</p>
              <p className="text-xs text-gray-500 flex items-center gap-1 mb-3"><MapPin className="w-3 h-3" /> Langston Golf Course</p>
              <div className="flex items-center gap-1.5 mb-3">
                {['RW', 'TC', 'JP', 'BM'].map((init, i) => (
                  <div key={i} className="w-7 h-7 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-[9px] font-bold text-emerald-300">{init}</div>
                ))}
                <span className="text-[10px] text-gray-500 ml-1">+7 joined</span>
              </div>
              <button onClick={handleGetStarted} className="w-full py-2.5 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs rounded-lg transition-colors">
                Reserve Spot
              </button>
            </div>
            )}

            {/* 6: LIVE — Eagle Alert (Chris L) */}
            {(feedTab === 'live' || feedTab === 'my') && (
            <div className={`bg-[#131a13] rounded-2xl border border-yellow-500/20 p-4 sm:p-5 hover:border-yellow-500/40 transition-all duration-500 ${isVisible['live-feed'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '400ms' }} data-testid="feed-card-eagle">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-full flex items-center justify-center text-white text-xs font-bold">CL</div>
                  <div>
                    <span className="text-sm font-bold">Chris L</span>
                    <span className="text-[10px] text-gray-500 block">8 min ago</span>
                  </div>
                </div>
                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-[10px] font-bold rounded-full flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" /> LIVE
                </span>
              </div>
              <p className="text-sm mb-1"><span className="text-yellow-400 font-black">EAGLE</span> <span className="font-bold">on Hole 14!</span></p>
              <p className="text-xs text-gray-500 flex items-center gap-1 mb-3"><MapPin className="w-3 h-3" /> Renditions Golf Course</p>
              <div className="flex items-center gap-4 text-gray-500 border-t border-white/5 pt-2">
                <button className="flex items-center gap-1 text-xs hover:text-emerald-400 transition-colors"><ThumbsUp className="w-3.5 h-3.5" /> 41</button>
                <button className="flex items-center gap-1 text-xs hover:text-emerald-400 transition-colors"><Flame className="w-3.5 h-3.5" /> 33</button>
                <button className="flex items-center gap-1 text-xs hover:text-emerald-400 transition-colors"><MessageSquare className="w-3.5 h-3.5" /> 12</button>
              </div>
            </div>
            )}

            {/* 7: MY GAMES — Group Finished (DMV Bundle Crew) */}
            {(feedTab === 'live' || feedTab === 'my' || feedTab === 'results') && (
            <div className={`bg-[#131a13] rounded-2xl border border-white/5 p-4 sm:p-5 hover:border-emerald-500/30 transition-all duration-500 ${isVisible['live-feed'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '480ms' }} data-testid="feed-card-group">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white text-[10px] font-bold">DMV</div>
                <div>
                  <span className="text-sm font-bold">DMV Bundle Crew</span>
                  <span className="text-[10px] text-gray-500 block">just finished</span>
                </div>
              </div>
              <p className="text-sm text-gray-300 mb-3">"Great competition today. Devin went off on the back 9"</p>
              <div className="flex items-center gap-1.5 mb-3">
                {[1,2,3,4].map(i => (
                  <div key={i} className="w-7 h-7 rounded-full bg-white/10 border border-white/10" />
                ))}
                <span className="text-[10px] text-gray-500 ml-1">4 players</span>
              </div>
              <div className="flex items-center gap-4 text-gray-500">
                <button className="flex items-center gap-1 text-xs hover:text-emerald-400 transition-colors"><ThumbsUp className="w-3.5 h-3.5" /> 31</button>
                <button className="flex items-center gap-1 text-xs hover:text-emerald-400 transition-colors"><MessageSquare className="w-3.5 h-3.5" /> 9</button>
              </div>
            </div>
            )}

            {/* 8: RESULTS — Skins Payout */}
            {(feedTab === 'live' || feedTab === 'results') && (
            <div className={`bg-[#131a13] rounded-2xl border border-white/5 p-4 sm:p-5 hover:border-emerald-500/30 transition-all duration-500 ${isVisible['live-feed'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '560ms' }} data-testid="feed-card-skins">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-1"><Flag className="w-3 h-3" /> Skins Payout</span>
                <span className="text-[10px] text-gray-500">4h ago</span>
              </div>
              <p className="text-sm font-bold mb-2">Wednesday Bundle — $240 pot</p>
              <div className="space-y-1.5 mb-3">
                {[
                  { name: 'Brandon T', skins: 4, payout: '$120' },
                  { name: 'Andre S', skins: 2, payout: '$60' },
                  { name: 'Eric M', skins: 2, payout: '$60' },
                ].map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-gray-300">{p.name} <span className="text-gray-600">({p.skins} skins)</span></span>
                    <span className="font-bold text-emerald-400">{p.payout}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 text-gray-500">
                <button className="flex items-center gap-1 text-xs hover:text-emerald-400 transition-colors"><ThumbsUp className="w-3.5 h-3.5" /> 16</button>
                <button className="flex items-center gap-1 text-xs hover:text-emerald-400 transition-colors"><Flame className="w-3.5 h-3.5" /> 7</button>
              </div>
            </div>
            )}

          </div>

          {/* See More */}
          <div className="text-center mt-8">
            <button onClick={handleGetStarted} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/5 border border-white/10 text-sm text-gray-400 hover:text-white hover:border-white/20 transition-all">
              See more activity <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ===== SECTION 3: WHAT YOU CAN DO ===== */}
      <section className="py-16" id="what-you-can-do" data-animate style={{ background: '#0e140e' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-emerald-400 text-xs font-bold uppercase tracking-widest">What You Can Do</span>
            <h2 className="text-2xl sm:text-3xl font-black mt-2">One App. Every Part of Your Golf Life.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: <MapPin className="w-6 h-6" />, title: 'Find Games', items: ['Discover games near you', 'Join instantly', 'Play anywhere'] },
              { icon: <Trophy className="w-6 h-6" />, title: 'Run Games', items: ['Organize tournaments', 'Track scores automatically', 'Manage players'] },
              { icon: <MessageCircle className="w-6 h-6" />, title: 'Stay Connected', items: ['Chat with golfers', 'Share results', 'Build your network'] },
              { icon: <Plane className="w-6 h-6" />, title: 'Travel & Play', items: ['Bucket list dream courses', 'Plan group golf trips', 'Explore top destinations'] },
            ].map((col, i) => (
              <div key={i} className={`p-6 rounded-2xl bg-[#131a13] border border-white/5 hover:border-emerald-500/20 transition-all duration-500 ${isVisible['what-you-can-do'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${i * 120}ms` }}>
                <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-400 mb-4">{col.icon}</div>
                <h3 className="text-lg font-black mb-3">{col.title}</h3>
                <ul className="space-y-2">
                  {col.items.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-400">
                      <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 4: CORE FEATURES ===== */}
      <section className="py-16" id="core-feat" data-animate>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-emerald-400 text-xs font-bold uppercase tracking-widest">Core Features</span>
            <h2 className="text-2xl sm:text-3xl font-black mt-2">Built for How You Actually Play</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { icon: <BarChart3 className="w-5 h-5" />, label: 'Live Leaderboard', desc: 'Real-time scoring every round' },
              { icon: <Camera className="w-5 h-5" />, label: 'Scorecard Scan', desc: 'AI reads your card instantly' },
              { icon: <Zap className="w-5 h-5" />, label: 'Auto Scoring', desc: 'Handicap & stats calculated' },
              { icon: <Calendar className="w-5 h-5" />, label: 'Event Calendar', desc: 'Never miss a tournament' },
              { icon: <Plane className="w-5 h-5" />, label: 'Golf Travel', desc: 'Plan group trips & getaways' },
              { icon: <Map className="w-5 h-5" />, label: 'Bucket List', desc: 'Track dream courses to play' },
            ].map((f, i) => (
              <div key={i} className={`p-5 rounded-2xl bg-[#131a13] border border-white/5 text-center hover:border-emerald-500/20 transition-all duration-500 ${isVisible['core-feat'] ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="w-10 h-10 mx-auto mb-3 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-400">{f.icon}</div>
                <h4 className="font-bold text-sm mb-1">{f.label}</h4>
                <p className="text-[11px] text-gray-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 5: THE MISSION ===== */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={SUNSET_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/80" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-4xl font-black leading-tight">
              Welcome to The Bundle Golf Club — <span className="text-emerald-400">Where Golf Gets Bigger</span>
            </h2>
          </div>
          <div className="space-y-4 text-gray-300 text-sm sm:text-base leading-relaxed">
            <p>Most golfers play in small circles. Same group. Same games. Same limits.</p>
            <p className="text-white font-semibold">We built The Bundle Golf Club to change that.</p>
            <p>By connecting local golf groups into one private, competitive network, you gain access to more games, bigger events, and greater opportunities—both on and off the course.</p>
            <p>As a member, you'll be part of a growing community where you can:</p>
            <ul className="space-y-2 pl-1">
              {[
                'Compete in organized games and events',
                'Track and grow your money games',
                'Access potential discounts on golf, travel, gear, and lessons',
                'Build real relationships and business connections',
                'Discover new formats and elevate how you play',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-emerald-400 font-semibold pt-2">There's power in numbers—and when golfers come together, the game changes.</p>
            <p className="text-white font-black text-lg sm:text-xl pt-2">Stop playing small. Step into a bigger game.</p>
          </div>
          <div className="text-center mt-8">
            <button onClick={handleGetStarted} className="px-8 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm rounded-xl transition-colors">
              Request Invitation
            </button>
          </div>
        </div>
      </section>

      {/* ===== SECTION 6: WHO IT'S FOR ===== */}
      <section className="py-16" id="who-for" data-animate style={{ background: '#0e140e' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-emerald-400 text-xs font-bold uppercase tracking-widest">Who It's For</span>
            <h2 className="text-2xl sm:text-3xl font-black mt-2">Your Entry Point</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-5">
            {[
              { icon: <Users className="w-7 h-7" />, title: 'Players', sub: 'Find and join games near you', cta: 'Join as a Golfer', action: handleGetStarted, accent: '#10b981' },
              { icon: <Crown className="w-7 h-7" />, title: 'Directors', sub: 'Run your own golf group', cta: 'Become a Director', action: () => navigate('/become-admin'), accent: '#eab308' },
              { icon: <Heart className="w-7 h-7" />, title: 'Fundraisers', sub: 'Host and grow your golf event', cta: 'Start a Fundraiser', action: () => navigate('/fundraiser'), accent: '#ef4444' },
              { icon: <Briefcase className="w-7 h-7" />, title: 'Sponsors', sub: 'Grow your business through golf', cta: 'Become a Sponsor', action: () => navigate('/sponsors'), accent: '#3b82f6', testid: 'landing-sponsor-tile' },
            ].map((c, i) => (
              <div key={i} onClick={c.action} data-testid={c.testid}
                className={`p-6 rounded-2xl bg-[#131a13] border border-white/5 hover:border-white/20 cursor-pointer group transition-all duration-500 ${isVisible['who-for'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${i * 120}ms` }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white mb-4" style={{ background: c.accent }}>{c.icon}</div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">{c.title}</div>
                <h3 className="text-lg font-black mb-4">{c.sub}</h3>
                <div className="flex items-center gap-1 text-sm font-bold group-hover:gap-2 transition-all" style={{ color: c.accent }}>
                  {c.cta} <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 7: SOCIAL PROOF / ENERGY ===== */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
            <Flame className="w-3.5 h-3.5 text-orange-400" />
            <span className="text-[11px] text-emerald-300 font-medium">This is what it looks like</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-black leading-snug mb-6">
            You've got 20+ golfers showing up every week.
            <br className="hidden sm:block" />
            The leaderboard is <span className="text-emerald-400">live</span>.
            <br className="hidden sm:block" />
            The competition is <span className="text-emerald-400">real</span>.
          </h2>
          <p className="text-gray-400 text-base max-w-lg mx-auto">
            And your group becomes the game everyone wants in.
          </p>
        </div>
      </section>

      {/* ===== SECTION 8: FINAL CTA ===== */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #064e3b 0%, #0b0f0b 70%)' }}>
        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-5xl font-black mb-4">
            Start Playing.
            <span className="text-emerald-400"> Start Connecting.</span>
          </h2>
          <p className="text-gray-400 mb-8 text-sm">
            Launch your first game in 7 days. <span className="text-yellow-400 font-bold">Limited spots per city.</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button data-testid="final-cta-find-game" onClick={handleGetStarted}
              className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm px-8 py-5 rounded-xl shadow-lg shadow-emerald-500/20 group">
              Join as a Golfer <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button onClick={() => navigate('/become-admin')} variant="outline"
              className="border-white/20 text-white hover:bg-white/10 font-semibold text-sm px-8 py-5 rounded-xl">
              Apply as Admin
            </Button>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-10 pt-6 border-t border-white/10">
            {[
              { icon: <Shield className="w-4 h-4" />, label: 'Invite Only' },
              { icon: <Zap className="w-4 h-4" />, label: '7-Day Launch' },
              { icon: <Clock className="w-4 h-4" />, label: 'Limited Spots' },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-1.5 text-gray-500 text-xs">
                <span className="text-emerald-400">{b.icon}</span> {b.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="py-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-emerald-500 rounded-lg flex items-center justify-center"><Trophy className="w-3.5 h-3.5 text-black" /></div>
            <span className="font-bold text-xs">The Bundle Golf Club</span>
          </div>
          <div className="flex gap-5 text-xs text-gray-500">
            <a href="https://thebundlegolfclubusa.com" className="hover:text-white transition-colors">Main Site</a>
            <a href="/become-admin" className="hover:text-white transition-colors">Directors</a>
            <a href="/fundraiser" className="hover:text-white transition-colors">Fundraisers</a>
            <a href="/sponsors" className="hover:text-white transition-colors">Sponsors</a>
          </div>
        </div>
        <div className="text-center text-[10px] text-gray-600 mt-6">&copy; 2026 The Bundle Golf Club</div>
      </footer>

      {/* Mobile Quick Links */}
      <div className="md:hidden fixed top-[3.5rem] left-0 right-0 z-40 bg-[#0b0f0b]/95 backdrop-blur-sm border-b border-white/5">
        <div className="flex gap-2 px-3 py-1.5">
          <button onClick={() => navigate('/become-admin')} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-bold">
            <Crown className="w-3 h-3" /> Become Director
          </button>
          <button onClick={() => navigate('/fundraiser')} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-400 text-[10px] font-bold">
            <Heart className="w-3 h-3" /> Fundraiser
          </button>
        </div>
      </div>

      {/* Modals */}
      {showAuthModal && <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} onSuccess={handleAuthSuccess} initialTab={authTab} />}
      {showSignupModal && (
        <SignupModal
          isOpen={showSignupModal}
          onClose={() => { setShowSignupModal(false); setSignupPrefillCode(''); setSignupPrefill(null); }}
          onSuccess={handleAuthSuccess}
          onLoginClick={() => { setShowSignupModal(false); handleLogin(); }}
          prefillCode={signupPrefillCode}
          prefillData={signupPrefill}
        />
      )}
      {showInviteRequestModal && (
        <RequestInvitationModal
          isOpen={showInviteRequestModal}
          onClose={() => setShowInviteRequestModal(false)}
          onSignInClick={handleLogin}
          onVerified={({ invite_code, prefill }) => {
            setSignupPrefillCode(invite_code || '');
            setSignupPrefill(prefill || null);
            setShowInviteRequestModal(false);
            setShowSignupModal(true);
          }}
        />
      )}
    </div>
  );
}
